import { paths } from '@/generated/supplier-experience-api';
import { FbxEligibilityStatus } from '@/enterprise/features/fundbox/content/fundboxSharedContent';

type LendingOpportunitiesResponse =
  paths['/user/lending-opportunities']['get']['responses'][200]['content']['application/json'];
type LendingOpportunitiesData = LendingOpportunitiesResponse['data'];

export type LendingOpportunity = LendingOpportunitiesData['opportunities'][number];

/** Parses Fundbox `providerDetails.amount` (numeric string from `credit_amount`). */
export const parseFundboxCreditAmount = (amount: string | null | undefined): number | undefined => {
  if (amount === null || amount === undefined || amount.trim() === '') {
    return undefined;
  }

  const parsed = Number(amount);

  return Number.isFinite(parsed) ? parsed : undefined;
};

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Formats SEA `expirationDate` (`YYYY-MM-DD`) for Fundbox marketing copy (`MM/DD/YYYY`).
 * Uses string parsing to avoid timezone shifts from `Date` parsing.
 */
export const formatFundboxExpirationDateForDisplay = (isoDate: string): string | undefined => {
  const match = ISO_DATE_PATTERN.exec(isoDate);

  if (!match) {
    return undefined;
  }

  const [, year, month, day] = match;

  return `${month}/${day}/${year}`;
};

export const selectFundboxLendingOpportunity = (opportunities: LendingOpportunity[]): LendingOpportunity | undefined =>
  opportunities.find((opportunity) => opportunity.provider === 'FUNDBOX');

export const mapFundboxExpirationDate = (opportunity: LendingOpportunity | undefined): string | undefined => {
  if (opportunity?.expirationDate === undefined) {
    return undefined;
  }

  return formatFundboxExpirationDateForDisplay(opportunity.expirationDate);
};

/** Maps SEA lending opportunity status to Fundbox page eligibility status. */
export const mapFundboxEligibilityStatus = (opportunity: LendingOpportunity | undefined): FbxEligibilityStatus => {
  if (opportunity?.status === 'APPROVED') {
    return FbxEligibilityStatus.APPROVED;
  }

  return FbxEligibilityStatus.PREQUALIFIED;
};
